<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div
          class="card my-4"
          style="min-height: 500px; display: flex; flex-direction: column"
        >
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div
              class="bg-gradient-success shadow-success border-radius-lg pt-2 pb-3"
            >
              <h6 class="text-white text-capitalize ps-3">
                URP Submitted Files
              </h6>
              <p class="text-white text-sm ps-3 mb-0">
                View and download your submitted zipped files
              </p>
            </div>
          </div>

          <div class="card-body px-0 pb-2" style="flex: 1">
            <div class="table-responsive p-0">
              <!-- Loading  -->
              <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border text-success" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Loading files...</p>
              </div>

              <div v-else-if="hasError" class="text-center py-5">
                <i
                  class="fas fa-exclamation-triangle fa-3x text-warning mb-3"
                ></i>
                <h5 class="text-warning">Failed to load files</h5>
                <p class="text-muted">Please try again later.</p>
                <material-button
                  color="success"
                  size="sm"
                  @click="loadUrpFiles"
                >
                  <i class="fas fa-redo me-2"></i>
                  Retry
                </material-button>
              </div>

              <div
                v-else-if="!urpFiles || urpFiles.length === 0"
                class="text-center py-5"
              >
                <i class="fas fa-file-archive fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No files found</h5>
                <p class="text-muted">No URP files have been submitted yet</p>
              </div>

              <table v-else class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase">#</th>
                    <th class="text-uppercase">File ID</th>
                    <th class="text-uppercase">File Name</th>
                    <th class="text-uppercase">Upload Date</th>
                    <!-- <th class="text-uppercase text-center">Actions</th> -->
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(file, index) in paginatedFiles"
                    :key="file.file_id || index"
                    class="table-row-hover"
                  >
                    <td class="ps-4">
                      <p class="text-xs font-weight-bold mb-0">
                        {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                      </p>
                    </td>

                    <td>
                      <div class="d-flex px-2 py-1">
                        <div class="file-icon">
                          <i class="fas fa-file-archive"></i>
                        </div>
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-sm">
                            {{ file.file_id || "N/A" }}
                          </h6>
                        </div>
                      </div>
                    </td>

                    <td>
                      <p class="text-sm font-weight-bold mb-0">
                        {{ getFileName(file.file_name) }}
                      </p>
                      <p class="text-xs text-secondary mb-0">
                        {{ getFilePath(file.file_name) }}
                      </p>
                    </td>

                    <td>
                      <p class="text-sm font-weight-bold mb-0">
                        {{ formatDate(file.uploaded_at) }}
                      </p>
                      <p class="text-xs text-secondary mb-0">
                        {{ formatTime(file.uploaded_at) }}
                      </p>
                    </td>

                    <!-- <td class="text-center">
                      <button
                        @click="downloadFile(file)"
                        class="btn btn-link text-success px-3 mb-0"
                        title="Download File"
                      >
                        <i class="fas fa-download fa-lg"></i>
                      </button>
                    </td> -->
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Card Footer - Always shown -->
          <div class="card-footer px-3" style="margin-top: auto">
            <div v-if="urpFiles && urpFiles.length > 0">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <p class="mb-0 text-sm text-secondary">
                    Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
                    {{ Math.min(currentPage * itemsPerPage, urpFiles.length) }}
                    of {{ urpFiles.length }} entries
                  </p>
                </div>

                <!-- Material Pagination -->
                <material-pagination :color="'success'" :size="'sm'">
                  <material-pagination-item
                    :prev="true"
                    :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)"
                  />

                  <material-pagination-item
                    v-for="page in displayedPages"
                    :key="page"
                    :label="page.toString()"
                    :active="page === currentPage"
                    @click="changePage(page)"
                  />

                  <material-pagination-item
                    :next="true"
                    :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)"
                  />
                </material-pagination>
              </div>
            </div>
            <div v-else class="text-center text-muted">
              <small>No files to display</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialPagination from "@/components/MaterialPagination.vue";
import MaterialPaginationItem from "@/components/MaterialPaginationItem.vue";
import axios from "axios";

export default {
  name: "urp-files",
  components: {
    MaterialButton,
    MaterialPagination,
    MaterialPaginationItem,
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      urpFiles: [],

      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.urpFiles.length / this.itemsPerPage);
    },

    displayedPages() {
      const pages = [];
      const maxVisible = 5;

      if (this.totalPages <= 0) return pages;

      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    paginatedFiles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.urpFiles.slice(start, end);
    },
  },
  created() {
    this.loadUrpFiles();
  },
  methods: {
    async loadUrpFiles() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const response = await axios.get("/operator-urp-files/");

        console.log("URP files response:", response.data);

        if (
          response.data &&
          response.data.urp_files &&
          Array.isArray(response.data.urp_files)
        ) {
          this.urpFiles = response.data.urp_files;
          this.urpFiles.sort(
            (a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at),
          );
        } else {
          console.error("Unexpected response format:", response.data);
          this.urpFiles = [];
        }
      } catch (error) {
        console.error("Error loading URP files:", error);
        this.hasError = true;
        this.urpFiles = [];

        this.showNotification("Failed to load URP files", "error");
      } finally {
        this.isLoading = false;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },

    //Methods
    getFileName(filePath) {
      if (!filePath) return "N/A";
      const parts = filePath.split("/");
      return parts[parts.length - 1] || filePath;
    },

    getFilePath(filePath) {
      if (!filePath) return "";
      const parts = filePath.split("/");
      if (parts.length > 1) {
        parts.pop();
        return parts.join("/") + "/";
      }
      return "";
    },

    formatDate(dateTime) {
      if (!dateTime) return "N/A";
      try {
        const date = new Date(dateTime);
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        console.warn("Error formatting date:", dateTime, error);
        return "Invalid Date";
      }
    },

    formatTime(dateTime) {
      if (!dateTime) return "";
      try {
        const date = new Date(dateTime);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch (error) {
        console.warn("Error formatting time:", dateTime, error);
        return "";
      }
    },

    downloadFile(file) {
      // TODO: Implement download logic
      console.log("Downloading file:", file);
      this.showNotification(`Download started for ${file.file_id}`, "info");
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          warning: "alert-warning",
          info: "alert-info",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
          ${message}
          <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.remove();
      }, 3000);
    },
  },
};
</script>

<style scoped>
.container-fluid {
  min-height: calc(100vh - 60px);
}

.card {
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-responsive {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.text-center.py-5 {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 12px;
  background-color: #e8f5e9;
  border-radius: 50%;
  color: red;
}

.badge {
  font-size: 0.65em;
  font-weight: 600;
  padding: 0.25em 0.6em;
}

.bg-gradient-success {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

.table-row-hover {
  transition: all 0.2s ease;
}

.table-row-hover:hover {
  background-color: rgba(76, 175, 80, 0.05);
  transform: scale(1.002);
}

.card-footer {
  margin-top: auto;
  background-color: transparent;
  border-top: 1px solid #e9ecef;
  padding: 1rem 0;
}

.shadow-success {
  box-shadow:
    0 4px 20px 0 rgba(76, 175, 80, 0.14),
    0 7px 10px -5px rgba(76, 175, 80, 0.4);
}

.btn-link {
  transition: all 0.2s ease;
}

.btn-link:hover {
  transform: scale(1.1);
}

.btn-link:active {
  transform: scale(0.95);
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }

  .file-icon {
    width: 28px;
    height: 28px;
    margin-right: 8px;
  }
}
</style>
